import React from "react";
import { cn } from "@/lib/utils";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  text?: string;
}

export function LoadingSpinner({
  size = "md",
  className,
  text,
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: "h-5 w-5",
    md: "h-8 w-8",
    lg: "h-12 w-12",
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <div
        className={cn(
          "relative animate-spin rounded-full border-2 border-border",
          "border-t-primary",
          sizeClasses[size],
          className
        )}
      />
      {text && (
        <p className="mt-3 text-sm font-medium text-muted-foreground">{text}</p>
      )}
    </div>
  );
}

interface LoadingScreenProps {
  text?: string;
  fullScreen?: boolean;
}

export function LoadingScreen({ text, fullScreen = false }: LoadingScreenProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm p-8 rounded-2xl",
        // Every caller renders this as the whole page (auth gate, Suspense, a page's early return),
        // where no parent has a height, so h-full collapsed to the content and the spinner sat at
        // the top. Fill the viewport to centre it.
        fullScreen ? "fixed inset-0 z-50" : "min-h-screen w-full"
      )}
    >
      <div className="relative">
        <div className="h-16 w-16 rounded-full border-4 border-border border-t-transparent animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-8 w-8 rounded-full bg-primary/20 animate-pulse" />
        </div>
      </div>
      {text && (
        <p className="mt-6 text-base font-medium text-foreground">{text}</p>
      )}
    </div>
  );
}

export function LoadingDots({ className }: { className?: string }) {
  return (
    <div className={cn("flex space-x-1.5", className)}>
      <div className="h-2 w-2 rounded-full bg-muted animate-bounce [animation-delay:-0.3s]" />
      <div className="h-2 w-2 rounded-full bg-muted animate-bounce [animation-delay:-0.15s]" />
      <div className="h-2 w-2 rounded-full bg-muted animate-bounce" />
    </div>
  );
}

export function LoadingPulse({ className }: { className?: string }) {
  return (
    <div className="flex justify-center">
      <div className={cn("relative", className)}>
        <div className="h-12 w-12 rounded-full border-2 border-border" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-6 w-6 rounded-full bg-primary animate-ping opacity-75" />
        </div>
      </div>
    </div>
  );
}
